<template>
  <div ref="rootEl" class="drawing-canvas" :class="cssClass">
    <overlay-loader :active="disabled" />
    <drawing-canvas-toolbar
      :tools="tools"
      :colors="colors"
      :selected-tool="selectedTool"
      :pen-config="penConfig"
      :highlighter-config="highlighterConfig"
      :eraser-config="eraserConfig"
      :text-box-config="textBoxConfig"
      :zoom-percentage="zoomPercentage"
      :is-zoom-min="isZoomMin"
      :is-zoom-max="isZoomMax"
      :can-select="canSelect"
      :can-undo="canUndo"
      :can-redo="canRedo"
      :can-remove="canRemove"
      :can-forward="canForward"
      :can-backward="canBackward"
      :can-clear="canClear"
      @tool-selected="handleToolSelected"
      @drawing-action="handleDrawingAction"
      @tool-config-change="handleToolConfigChange"
    />

    <div ref="canvasGroup" class="canvas-group">
      <!-- This will be hidden by CSS in Tablet/Desktop and displayed only in Mobile view
           as the positioning in Mobile would be relatively to the canvas group instead
      -->
      <div
        class="zoom-actions"
        :class="{
          expanded: zoomActionsExpanded,
        }"
      >
        <button class="button toggle-expand" @click="toggleZoomActions">
          <svg-icon v-if="zoomActionsExpanded" name="ArrowRight24" kind="action" />
          <svg-icon v-else name="ArrowLeft24" kind="action" />
        </button>

        <template v-if="zoomActionsExpanded">
          <tooltip-button
            class="button zoom-fit"
            kind="ghost"
            size="small"
            icon-only
            :show-tooltip="true"
            :disabled="false"
            tip="Fit to Width"
            @click="handleZoomFitToWidth"
          >
            <svg-icon name="FitToWidth24" kind="action" />
          </tooltip-button>

          <button
            type="button"
            class="button zoom-out"
            :disabled="isZoomMin"
            @click="handleZoomAction(0)"
          >
            <svg-icon name="ZoomOut24" kind="action" />
          </button>

          <!-- Native input used instead of <cv-slider> as the latter doesn't support dragging on Mobile devices -->
          <input
            class="zoom-slider"
            type="range"
            :value="zoomPercentage"
            :min="minZoom"
            :max="maxZoom"
            @change="handleZoomSliderChange"
          />

          <button
            type="button"
            class="button zoom-in"
            :disabled="isZoomMax"
            @click="handleZoomAction(1)"
          >
            <svg-icon name="ZoomIn24" kind="action" />
          </button>
        </template>
      </div>
      <div class="canvas-wrapper free-drawing" @click="handleCanvasClick">
        <canvas id="freeDrawingCanvas"></canvas>
        <div
          v-show="selectedTool === DRAWING_TOOL.ERASER && eraserOutlineVisible"
          class="eraser-outline"
          :style="eraserOutlineStyle"
        ></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, markRaw, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import {
  ActiveSelection,
  Canvas,
  CanvasEvents,
  FabricImage,
  FabricObject,
  Path,
  PencilBrush,
  Point,
  Rect,
  Textbox,
} from "fabric";
import {
  BRUSH_OPACITY,
  CANVAS_ACTION,
  CANVAS_ERROR,
  CANVAS_SIZE,
  CANVAS_TASK,
  DRAWING_TOOL,
} from "@plugins/drawing/drawing.constants";
import {
  CANVAS_COLORS,
  CANVAS_FILE_SETTINGS,
  CANVAS_SETTINGS,
  CANVAS_STROKE_SETTINGS,
  CANVAS_TEXT_SETTINGS,
  CANVAS_TOOLS,
} from "@plugins/drawing/drawing.config";
import DrawingCanvasToolbar from "@plugins/drawing/components/DrawingCanvasToolbar.vue";
import DOMUtils from "@utils/dom.utils";
import ColorUtils from "@utils/color.utils";
import { isDefined, isEmpty } from "@utils/var.check";
import { debounceFn } from "@/utils/debounce";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import TooltipButton from "@plugins/common/components/TooltipButton.vue";
import OverlayLoader from "@plugins/common/components/OverlayLoader.vue";
import FileUploader from "@plugins/file-uploader-v2/components/FileUploader.vue";
import { useI18n } from "vue-i18n";
import {
  BrushConfig,
  ColorItem,
  EraserConfig,
  TextBoxConfig,
  ToolConfig,
  ToolItem,
} from "@plugins/drawing/types.ts";
import drawingUtils from "@plugins/drawing/drawing.utils";
import { FileStatus, FileUploadEntry, UploadFlow } from "@plugins/file-uploader-v2/types.ts";
import FileUploaderModal from "@plugins/file-uploader-v2/components/FileUploaderModal.vue";
import randomUtils from "@utils/random.utils.ts";
import { useModal } from "@plugins/modal/composables/modal-instance.ts";
import { EraserBrush } from "@erase2d/fabric";
import { useStore } from "vuex";

const props = withDefaults(
  defineProps<{
    width?: number;
    height?: number;
    tools?: ToolItem[];
    colors?: ColorItem[];
    initialTool?: string;
    initialColor?: string;
    backgroundColor?: string;
    strokeSettings?: Record<string, unknown>;
    textBoxSettings?: Record<string, unknown>;
    backgroundImageUrl?: string;
    exportFileType?: string;
    exportImageScaleValue?: number;
    task?: string;
  }>(),
  {
    width: CANVAS_SETTINGS.width,
    height: CANVAS_SETTINGS.height,
    tools: () => CANVAS_TOOLS as ToolItem[],
    colors: () => CANVAS_COLORS as ColorItem[],
    initialTool: CANVAS_SETTINGS.defaultTool,
    initialColor: "",
    backgroundColor: CANVAS_SETTINGS.backgroundColor,
    strokeSettings: () => CANVAS_STROKE_SETTINGS as Record<string, unknown>,
    textBoxSettings: () => CANVAS_TEXT_SETTINGS as Record<string, unknown>,
    backgroundImageUrl: "",
    exportFileType: CANVAS_FILE_SETTINGS.defaultFileType,
    exportImageScaleValue: CANVAS_FILE_SETTINGS.imageScaleMultiplier,
    task: CANVAS_TASK.DEFAULT,
  },
);

interface MultiTouchState {
  distance: number;
  midpoint: {
    x: number;
    y: number;
  };
  initialZoom: number;
}

interface PrevToolState {
  tool: string;
  isDrawingMode: boolean;
  selection: boolean;
}

interface FabricTextBoxSettings {
  fill?: string;
  erasable?: boolean;
  left?: number;
  top?: number;
  width?: number;

  [key: string]: unknown;
}

interface CanvasObjectWithBaseImageFlag extends FabricObject {
  isBaseImage?: boolean;
}

const emit = defineEmits<{
  (e: "isDirty", payload: { isDirty: boolean; isClear: boolean }): void;
  (e: "nextData", payload: Blob): void;
  (e: "ready"): void;
  (e: "error", payload: string): void;
}>();

const modal = useModal();
const store = useStore();
const { t } = useI18n();

const rootEl = ref<HTMLElement | null>(null);
const fileUpload = ref<InstanceType<typeof FileUploader> | null>(null);

let fabricCanvas: Canvas | null = null;
const canvas = ref<Canvas | undefined>(undefined);
const eraser = ref<EraserBrush | undefined>(undefined);
const brush = ref<PencilBrush | undefined>(undefined);
const bgImage = ref<FabricImage | undefined>(undefined);
const reader = ref<FileReader | undefined>(undefined);
const selectedTool = ref<string>(props.initialTool);
const isCanvasInitialising = ref<boolean>(true);
const isCanvasObjSelected = ref<boolean>(false);
const isDrawingEnabled = ref<boolean>(true);
const isDirty = ref<boolean>(false);
const isMoveEnabled = ref<boolean>(false);
const state = ref<{
  initial: Record<string, unknown> | undefined;
  history: Record<string, unknown>[];
  undoHistory: Record<string, unknown>[];
  cache: Record<string, unknown>[];
}>({
  initial: undefined,
  history: [],
  undoHistory: [],
  cache: [],
});
const zoomActionsExpanded = ref<boolean>(true);

const isDragging = ref<boolean>(false);
const lastPosX = ref<number>(0);
const lastPosY = ref<number>(0);
const isDrawing = ref<boolean>(false);
const isDrawingInvalid = ref<boolean>(false);
const wrapper = ref<HTMLCanvasElement | undefined>(undefined);

const prevMultiTouchState = ref<MultiTouchState>({
  distance: 0,
  midpoint: {
    x: 0,
    y: 0,
  },
  initialZoom: 1,
});
const initialMultiTouchViewportPoint = ref({ x: 0, y: 0 });
const prevToolState = ref<PrevToolState>({
  tool: "",
  isDrawingMode: false,
  selection: true,
});
const uploadedFile = ref<FileUploadEntry | null>(null);
const currentZoom = ref<number>(CANVAS_SIZE.ZOOM_DEFAULT);
const eraserOutlinePosition = ref({ x: 0, y: 0 });
const eraserOutlineVisible = ref(false);
const isTwoFingerGesture = ref(false);
const activeTouchCount = ref(0);

// Track separate configs for each tool
const penConfig = ref<BrushConfig>({ width: 5, color: "" });
const highlighterConfig = ref<BrushConfig>({
  width: 20,
  color: CANVAS_SETTINGS.defaultHighlighterColor,
});
const eraserConfig = ref<EraserConfig>({ width: 20 });
const textBoxConfig = ref<TextBoxConfig>({ color: "" });

const canSelect = computed(() => {
  // can select if there are strokes
  return !isHistoryEmpty();
});

const canUndo = computed(() => {
  // can undo if there are strokes or cache
  return (!isHistoryEmpty() && !initialImageLoad.value) || !isCacheEmpty();
});

const canRedo = computed(() => {
  // can redo if there are undo action
  return !isUndoHistoryEmpty();
});

const canRemove = computed(() => {
  return isCanvasObjSelected.value;
});

const canForward = computed(() => {
  return isCanvasObjSelected.value;
});

const canBackward = computed(() => {
  return isCanvasObjSelected.value;
});

const canClear = computed(() => {
  // can clear if there are strokes
  return !isHistoryEmpty();
});

/**
 * Canvas zoom value. Default value 1.
 */
const canvasZoomSize = computed(() => {
  return currentZoom.value;
});

/**
 * Canvas zoom size in percentage value.
 */
const zoomPercentage = computed(() => {
  return parseInt(String(canvasZoomSize.value * CANVAS_SIZE.ZOOM_PERCENTAGE));
});

const isZoomMin = computed(() => {
  return zoomPercentage.value === CANVAS_SIZE.ZOOM_MIN;
});

const isZoomMax = computed(() => {
  return zoomPercentage.value === CANVAS_SIZE.ZOOM_MAX;
});

const minZoom = computed(() => {
  return CANVAS_SIZE.ZOOM_MIN;
});

const maxZoom = computed(() => {
  return CANVAS_SIZE.ZOOM_MAX;
});

/**
 * Center of canvas boundary (actual usable area).
 */
const canvasBoundaryCenterPoint = computed(() => {
  return new Point(CANVAS_SIZE.BOUNDARY_WIDTH / 2, CANVAS_SIZE.BOUNDARY_HEIGHT / 2);
});

/**
 * Canvas is to be disabled when:
 *  1. canvas is initialising (background image is loading up)
 *  2. there is a task for this canvas component (check dirty & next)
 */
const disabled = computed(() => {
  return isCanvasInitialising.value || props.task !== CANVAS_TASK.DEFAULT;
});

const cssClass = computed(() => {
  return {
    "move-enabled": isMoveEnabled.value,
    "textbox-enabled": selectedTool.value === DRAWING_TOOL.TEXT_BOX,
    "eraser-enabled": selectedTool.value === DRAWING_TOOL.ERASER,
  };
});

const initialImageLoad = computed(() => {
  if (hasBackgroundImage()) {
    const first = state.value.history[0] as unknown as {
      objects: { src: string }[];
    };
    return (
      first?.objects?.length === 1 &&
      getImageSourceWithoutQuery(props.backgroundImageUrl) ===
        getImageSourceWithoutQuery(first?.objects?.[0]?.src ?? "") &&
      state.value.history.length === 1
    );
  } else return false;
});

const isTeacher = computed<boolean>(() => store.getters["auth/isTeacher"]);

const eraserOutlineStyle = computed(() => {
  const size = eraserConfig.value.width * currentZoom.value;
  return {
    width: `${size}px`,
    height: `${size}px`,
    left: `${eraserOutlinePosition.value.x}px`,
    top: `${eraserOutlinePosition.value.y}px`,
    transform: "translate(-50%, -50%)",
  };
});

// Touch event handlers - defined at component scope for cleanup
const handleTouchStart = (e: TouchEvent) => {
  const localCanvas = canvas.value as Canvas;
  if (!localCanvas) return;
  const previousTouchCount = activeTouchCount.value;
  activeTouchCount.value = e.touches.length;

  if (isTwoFingerGesture.value && e.touches.length < 2) {
    isTwoFingerGesture.value = false;
    restoreToolStateAfterGesture(localCanvas);
  }

  // Check for two-finger touch
  if (e.touches.length === 2 && previousTouchCount < 2 && !isTwoFingerGesture.value) {
    const t1 = e.touches[0];
    const t2 = e.touches[1];
    initializeTwoFingerGesture(localCanvas, t1, t2);

    e.preventDefault();
  }
};

const handleTouchMove = (ev: TouchEvent) => {
  const localCanvas = canvas.value as Canvas;
  if (!localCanvas) return;
  const previousTouchCount = activeTouchCount.value;
  activeTouchCount.value = ev.touches.length;

  if (ev.touches.length === 2 && previousTouchCount < 2 && !isTwoFingerGesture.value) {
    initializeTwoFingerGesture(localCanvas, ev.touches[0], ev.touches[1]);
  }

  if (ev.touches.length === 2 && isTwoFingerGesture.value) {
    ev.preventDefault();

    const t1 = ev.touches[0];
    const t2 = ev.touches[1];

    // Get current state
    const currentDistance = getDistance(t1, t2);
    const currentMidpoint = getMidpoint(t1, t2);

    // Calculate movement and distance change
    const deltaX = currentMidpoint.x - prevMultiTouchState.value.midpoint.x;
    const deltaY = currentMidpoint.y - prevMultiTouchState.value.midpoint.y;
    const panDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    const initialDistance = prevMultiTouchState.value.distance;
    const distanceChange = Math.abs(currentDistance - initialDistance);

    // Determine gesture intent: zoom vs pan
    // If distance change is significantly larger than pan movement, it's likely a pinch gesture
    // Require at least 20% distance change AND distance change > pan movement
    const ZOOM_THRESHOLD = 0.2; // 20% minimum distance change
    const GESTURE_RATIO_THRESHOLD = 1.5; // Distance change must be 1.5x larger than pan movement

    // Pan before zooming so zoomToPoint's anchor correction is the last translation applied.
    const vpt = localCanvas.viewportTransform!;
    vpt[4] += deltaX;
    vpt[5] += deltaY;

    if (initialDistance > 0) {
      const scaleFactor = currentDistance / initialDistance;
      const BASE_SCALE = 1.0;
      const scaleChange = Math.abs(scaleFactor - BASE_SCALE);

      // Only zoom if:
      // 1. Scale change exceeds threshold (20%)
      // 2. Distance change is significantly larger than panning movement
      const isPinchGesture =
        scaleChange > ZOOM_THRESHOLD &&
        (panDistance === 0 ||
          (panDistance > 0 && distanceChange / panDistance > GESTURE_RATIO_THRESHOLD));

      if (isPinchGesture) {
        // Apply damping factor to make zoom less sensitive
        const dampingFactor = 0.5;
        const dampedScaleFactor = BASE_SCALE + (scaleFactor - BASE_SCALE) * dampingFactor;

        let newZoom = prevMultiTouchState.value.initialZoom * dampedScaleFactor;

        const minZoomValue = CANVAS_SIZE.ZOOM_MIN / CANVAS_SIZE.ZOOM_PERCENTAGE;
        const maxZoomValue = CANVAS_SIZE.ZOOM_MAX / CANVAS_SIZE.ZOOM_PERCENTAGE;
        newZoom = Math.max(minZoomValue, Math.min(maxZoomValue, newZoom));

        const initialZoomAnchor = new Point(
          initialMultiTouchViewportPoint.value.x,
          initialMultiTouchViewportPoint.value.y,
        );

        localCanvas.zoomToPoint(initialZoomAnchor, newZoom);
        currentZoom.value = newZoom;
      }
    }

    handleCanvasViewportTranslation();
    localCanvas.requestRenderAll();

    // Update previous midpoint for next iteration (but keep initial distance and zoom)
    prevMultiTouchState.value.midpoint = currentMidpoint;
  } else if (isDragging.value && ev.touches.length === 1) {
    ev.preventDefault();
    handleCanvasPanningArea(ev);
    localCanvas.requestRenderAll();
    lastPosX.value = getClientX(ev);
    lastPosY.value = getClientY(ev);
  }
};

const handleTouchEnd = (e: TouchEvent) => {
  const localCanvas = canvas.value as Canvas;
  if (!localCanvas) return;
  activeTouchCount.value = e.touches.length;

  if (
    isTwoFingerGesture.value &&
    e.touches.length === 1 &&
    prevToolState.value.tool === CANVAS_ACTION.MOVE
  ) {
    localCanvas.setViewportTransform(localCanvas.viewportTransform!);
    isTwoFingerGesture.value = false;
    restoreToolStateAfterGesture(localCanvas);
    isDragging.value = true;
    lastPosX.value = getClientX(e);
    lastPosY.value = getClientY(e);
    return;
  }

  finalizeTwoFingerGesture(localCanvas, e.touches.length, false);
};

const handleTouchCancel = (e: TouchEvent) => {
  const localCanvas = canvas.value as Canvas;
  if (!localCanvas) return;
  activeTouchCount.value = e.touches.length;

  finalizeTwoFingerGesture(localCanvas, e.touches.length, true);
};

watch(
  () => props.task,
  () => {
    if (props.task === CANVAS_TASK.CHECK_DIRTY) {
      // send canvas dirty state and is clear state to parent
      emit("isDirty", {
        isDirty: isDirty.value,
        isClear: (fabricCanvas?.getObjects().length ?? 0) === 0,
      });
    } else if (props.task === CANVAS_TASK.NEXT) {
      // send canvas image file to parent
      emit("nextData", getCanvasImageFile());
    } else if (props.task === CANVAS_TASK.RELOAD) {
      // reload canvas to last save state and ready for continue edit
      const previousState = getLatestHistoryState();
      if (previousState != null) loadCanvasToState(previousState);
      emit("ready");
    }
  },
);

onMounted(() => {
  initCanvas();
  resizeCanvas();
  nextTick(() => {
    handleZoomDefault();
    const drawingCanvasModal = document.querySelector(".drawing-canvas-modal");
    if (drawingCanvasModal) {
      (drawingCanvasModal as HTMLElement).focus(); // remove text cursor on richtext editor & close mobile keyboard
    }
  });
});

onUnmounted(() => {
  // Clean up touch event listeners
  if (wrapper.value) {
    wrapper.value.removeEventListener("touchstart", handleTouchStart);
    wrapper.value.removeEventListener("touchmove", handleTouchMove);
  }

  // Keep on window: touch can end/cancel outside canvas wrapper on mobile
  window.removeEventListener("touchend", handleTouchEnd);
  window.removeEventListener("touchcancel", handleTouchCancel);
});

/**
 * Center of canvas (visible area).
 */
const calculateCanvasCenterPoint = () => {
  return new Point((canvas.value?.getWidth() ?? 0) / 2, (canvas.value?.getHeight() ?? 0) / 2);
};

/**
 * Function to initialize the canvas.
 */
function initCanvas() {
  const drawingArea = new Rect({
    width: CANVAS_SIZE.BOUNDARY_WIDTH,
    height: CANVAS_SIZE.BOUNDARY_HEIGHT,
    fill: CANVAS_SETTINGS.drawingArea,
    evented: false,
    selectable: false,
    originX: "left",
    originY: "top",
  });

  const el = rootEl.value as Element;
  const group = DOMUtils.elOf(el, ".canvas-group")[0] as HTMLElement;
  fabricCanvas = new Canvas("freeDrawingCanvas", {
    position: "absolute",
    backgroundColor: props.backgroundColor,
    width: group.clientWidth,
    height: group.clientHeight,
    isDrawingMode: true,
    selection: true,
    allowTouchScrolling: true,
    preserveObjectStacking: true,
    enableRetinaScaling: true,
  });

  canvas.value = fabricCanvas;
  wrapper.value = canvas.value.upperCanvasEl;

  drawingArea.canvas = fabricCanvas;
  fabricCanvas.backgroundImage = drawingArea as unknown as FabricImage;

  // Set default tool properties
  brush.value = new PencilBrush(fabricCanvas);
  eraser.value = new EraserBrush(fabricCanvas);
  eraser.value.strokeLineCap = "square";
  eraser.value.strokeLineJoin = "miter";
  fabricCanvas.freeDrawingBrush = brush.value;
  setDefaultTool();

  // apply canvas binding
  bindCanvasObjectModification();
  bindCanvasObjectSelection();
  bindCanvasStrokePath();
  bindCanvasResize();
  bindCanvasMouseAction();
  bindCanvasFocus();
  bindCanvasAttachImage();

  // set object properties globally
  FabricObject.prototype.set({
    transparentCorners: false,
  });

  if (hasBackgroundImage()) loadBackgroundImage();
  else {
    syncInitialCanvasState();
    setCanvasReady();
  }
}

/**
 * Set the canvas initialisation ready.
 */
function setCanvasReady() {
  isCanvasInitialising.value = false;
}

function syncInitialCanvasState() {
  const localCanvas = fabricCanvas as Canvas;
  state.value.initial = localCanvas.toDatalessJSON() as unknown as Record<string, unknown>;
  clearHistory();
  clearUndoHistory();
  clearCache();
}

function setBaseImageConfig(img: FabricImage) {
  (img as CanvasObjectWithBaseImageFlag).isBaseImage = true;
  img.set({
    selectable: false,
    evented: false,
    erasable: false,
    hoverCursor: "default",
  });
}

function setDefaultTool() {
  // Initialize pen and textBox config based on role
  const defaultColorConfig = isTeacher.value
    ? CANVAS_SETTINGS.defaultColorForTeacher
    : CANVAS_SETTINGS.defaultColorForStudent;

  penConfig.value.color = defaultColorConfig;
  textBoxConfig.value.color = defaultColorConfig;

  setSelectedDrawingTool(props.initialTool);

  // Determine initial color based on initial tool
  let initialColor = props.initialColor;
  if (!initialColor) {
    initialColor =
      props.initialTool === DRAWING_TOOL.HIGHLIGHTER
        ? CANVAS_SETTINGS.defaultHighlighterColor
        : defaultColorConfig;
  }

  setSelectedDrawingToolColor(initialColor);
  setDrawingMode(true);
}

/**
 *  Check if the canvas has been given a background image.
 */
function hasBackgroundImage() {
  return !!props.backgroundImageUrl;
}

function getImageSourceWithoutQuery(url: string) {
  return url.split("?")[0];
}

function findBaseImageObject(objects: FabricObject[]) {
  if (!hasBackgroundImage()) {
    return undefined;
  }

  const backgroundImageSource = getImageSourceWithoutQuery(props.backgroundImageUrl);
  return objects.find(
    (object): object is FabricImage =>
      object instanceof FabricImage &&
      getImageSourceWithoutQuery(object.getSrc()) === backgroundImageSource,
  );
}

/**
 *  Check if url is a blob.
 */
function isBlobUrl(url: string) {
  return url.startsWith("blob:");
}

/**
 * Function to load the background image.
 */
function loadBackgroundImage() {
  const localCanvas = fabricCanvas as Canvas;
  if (!hasBackgroundImage()) return;

  if (bgImage.value) {
    // cached background image exist, use it directly.
    addBaseImageToCanvas(bgImage.value as FabricImage);
    localCanvas.discardActiveObject();
    localCanvas.renderAll();
    setDefaultTool();
    setCanvasReady();
    return;
  }

  const isBlob = isBlobUrl(props.backgroundImageUrl);

  /**
   * IMPORTANT NOTE:
   * Since the media files are stored in a different domain, the CORS policy applies here.
   * This is especially so in the case of canvas drawing since the file will be manipulated.
   * Hence, in order to retrieve a trusted source, we have to set the "use-credentials" flag.
   *
   * Also, AWS CloudFront caches the files, and this caused some issues with CORS.
   * Therefore the image URL are appended with a time-stamp to force the retrieval of the
   * actual file copy.
   */

  const versionedImageUrl = isBlob
    ? props.backgroundImageUrl
    : props.backgroundImageUrl.replace(/\?t=\d*/, "").concat("?t=" + new Date().getTime());

  const fromUrlOptions = isBlob ? {} : { crossOrigin: "use-credentials" as const };

  FabricImage.fromURL(versionedImageUrl, fromUrlOptions).then((img) => {
    // case when the background image is not successfully loaded
    if (!img._element) {
      // case when the background image is not successfully loaded
      emit("error", CANVAS_ERROR.BACKGROUND_LOAD_FAILED);
      return;
    }
    bgImage.value = img;
    addBaseImageToCanvas(img);
    localCanvas.discardActiveObject();
    localCanvas.renderAll();
    setDefaultTool();
    setCanvasReady();
  });
}

/**
 * Handle binding on canvas modification.
 */
function bindCanvasObjectModification() {
  const localCanvas = fabricCanvas as Canvas;

  localCanvas.on("object:modified", (e: CanvasEvents["object:modified"]) => {
    saveCanvasState();

    const aCoords = e.target.aCoords!;
    let isCoordsInvalid = false;
    if (aCoords.tl.x < 0 || aCoords.tl.x > CANVAS_SIZE.BOUNDARY_WIDTH) isCoordsInvalid = true;
    if (aCoords.tl.y < 0 || aCoords.tl.y > CANVAS_SIZE.BOUNDARY_HEIGHT) isCoordsInvalid = true;
    if (aCoords.tr.x < 0 || aCoords.tr.x > CANVAS_SIZE.BOUNDARY_WIDTH) isCoordsInvalid = true;
    if (aCoords.tr.y < 0 || aCoords.tr.y > CANVAS_SIZE.BOUNDARY_HEIGHT) isCoordsInvalid = true;
    if (aCoords.bl.x < 0 || aCoords.bl.x > CANVAS_SIZE.BOUNDARY_WIDTH) isCoordsInvalid = true;
    if (aCoords.bl.y < 0 || aCoords.bl.y > CANVAS_SIZE.BOUNDARY_HEIGHT) isCoordsInvalid = true;
    if (aCoords.br.x < 0 || aCoords.br.x > CANVAS_SIZE.BOUNDARY_WIDTH) isCoordsInvalid = true;
    if (aCoords.br.y < 0 || aCoords.br.y > CANVAS_SIZE.BOUNDARY_HEIGHT) isCoordsInvalid = true;

    if (isCoordsInvalid) {
      state.value.history.pop();
      const previousState = getLatestHistoryState();
      if (previousState != null) loadCanvasToState(previousState);
    }
  });

  localCanvas.on(
    "object:scaling",
    debounceFn(
      () => {
        saveCanvasState();
      },
      500,
      {
        leading: false,
        trailing: true,
      },
    ),
  );

  localCanvas.on("object:moving", (e: CanvasEvents["object:moving"]) => {
    // obj.left & obj.top values are object position within canvas boundaries (actual usable area)
    // obj.getBoundingRect() values are object displayed on canvas (visible area) currently,
    // thus its width & height changes with zoom, left & top depends on canvas visible area
    const obj = e.target;
    const objWidth = obj.getBoundingRect().width;
    const objHeight = obj.getBoundingRect().height;
    const objPosition = {
      left: obj.left!,
      top: obj.top!,
      right: obj.left! + objWidth,
      bottom: obj.top! + objHeight,
    };
    if (objPosition.left < 0) obj.left = 0;
    if (objPosition.top < 0) obj.top = 0;
    if (objPosition.right > CANVAS_SIZE.BOUNDARY_WIDTH)
      obj.left = CANVAS_SIZE.BOUNDARY_WIDTH - objWidth;
    if (objPosition.bottom > CANVAS_SIZE.BOUNDARY_HEIGHT)
      obj.top = CANVAS_SIZE.BOUNDARY_HEIGHT - objHeight;
    obj.setCoords();
  });
}

/**
 * Handle binding on canvas object selected.
 */
function bindCanvasObjectSelection() {
  const localCanvas = canvas.value as Canvas;
  localCanvas.on("selection:created", () => {
    isCanvasObjSelected.value = true;
  });

  localCanvas.on("selection:cleared", () => {
    isCanvasObjSelected.value = false;
    handleCanvasObjUnselected();
  });

  localCanvas.on("selection:updated", () => {
    handleCanvasObjUnselected();
  });
}

/**
 * Handle binding on canvas strokes/path
 */
function bindCanvasStrokePath() {
  const localCanvas = fabricCanvas as Canvas;
  const localEraser = eraser.value as EraserBrush;

  localEraser.on("end", async (e) => {
    // prevent from committing erasing to the tree
    e.preventDefault();

    const { path, targets } = e.detail;
    await localEraser.commit({ path, targets });
    saveCanvasState();
  });

  localCanvas.on("path:created", (ev) => {
    const path = (ev as unknown as { path: Path }).path;
    path.set(props.strokeSettings);
    saveCanvasState();
  });
}

/**
 * Handle binding on window resize to resize the canvas accordingly.
 */
function bindCanvasResize() {
  window.onresize = debounceFn(() => resizeCanvas(), CANVAS_SETTINGS.resizeDebouncePeriod);
}

function getDistance(t1: Touch, t2: Touch) {
  const dx = t1.clientX - t2.clientX;
  const dy = t1.clientY - t2.clientY;
  return Math.hypot(dx, dy);
}

function getMidpoint(t1: Touch, t2: Touch) {
  return {
    x: (t1.clientX + t2.clientX) / 2,
    y: (t1.clientY + t2.clientY) / 2,
  };
}

function getCanvasViewportPoint(canvas: Canvas, clientPoint: MultiTouchState["midpoint"]) {
  // Bypass Fabric's active-event pointer cache for the calculated touch midpoint.
  const bounds = canvas.upperCanvasEl.getBoundingClientRect();
  const scaleX = bounds.width > 0 ? canvas.getWidth() / bounds.width : 1;
  const scaleY = bounds.height > 0 ? canvas.getHeight() / bounds.height : 1;

  return {
    x: (clientPoint.x - bounds.left) * scaleX,
    y: (clientPoint.y - bounds.top) * scaleY,
  };
}
function initializeTwoFingerGesture(localCanvas: Canvas, t1: Touch, t2: Touch) {
  if (!isTwoFingerGesture.value) {
    prevToolState.value = {
      tool: selectedTool.value,
      isDrawingMode: localCanvas.isDrawingMode,
      selection: localCanvas.selection,
    };
  }

  isTwoFingerGesture.value = true;
  isDrawing.value = false;
  localCanvas.isDrawingMode = false;
  localCanvas.selection = false;

  const midpoint = getMidpoint(t1, t2);
  initialMultiTouchViewportPoint.value = getCanvasViewportPoint(localCanvas, midpoint);
  prevMultiTouchState.value = {
    distance: getDistance(t1, t2),
    midpoint,
    initialZoom: localCanvas.getZoom(),
  };
}

function restoreToolStateAfterGesture(localCanvas: Canvas) {
  const previousTool = prevToolState.value.tool;
  selectedTool.value = previousTool;

  if (isFreeDrawingBrush(previousTool)) {
    if (previousTool === DRAWING_TOOL.ERASER) {
      localCanvas.freeDrawingBrush = eraser.value as EraserBrush;
    } else {
      localCanvas.freeDrawingBrush = brush.value as PencilBrush;
    }
  }

  localCanvas.isDrawingMode = prevToolState.value.isDrawingMode;
  localCanvas.selection = prevToolState.value.selection;

  const isDrawingTool = isFreeDrawingBrush(previousTool);
  const isMoveSelected = previousTool === CANVAS_ACTION.MOVE;
  const isObjectsSelectable = !isDrawingTool && !isMoveSelected;

  localCanvas.getObjects().forEach((object) => {
    if ((object as CanvasObjectWithBaseImageFlag).isBaseImage) {
      setBaseImageConfig(object as FabricImage);
      return;
    }
    object.selectable = isObjectsSelectable;
    object.evented = isObjectsSelectable;
    object.hoverCursor = isMoveSelected ? "pointer" : "move";
  });

  localCanvas.requestRenderAll();
}

function finalizeTwoFingerGesture(
  localCanvas: Canvas,
  activeTouchCount: number,
  isCancelled: boolean,
) {
  if (!isTwoFingerGesture.value) {
    return;
  }

  const isGestureEnded = activeTouchCount === 0 || isCancelled;
  if (!isGestureEnded) {
    return;
  }

  localCanvas.setViewportTransform(localCanvas.viewportTransform!);
  isTwoFingerGesture.value = false;
  restoreToolStateAfterGesture(localCanvas);
}

/**
 * Handle binding on canvas mouse action.
 */
function bindCanvasMouseAction() {
  const localCanvas = canvas.value as Canvas;
  localCanvas.on("mouse:down", (option: CanvasEvents["mouse:down"]) => {
    const event = option.e;
    event.preventDefault();

    // Prevent drawing during two-finger gestures
    if (isTwoFingerGesture.value) {
      const isTouchEvent = "touches" in event && Array.isArray(event.touches);
      const activeTouches = isTouchEvent ? event.touches.length : 0;
      if (activeTouches < 2) {
        isTwoFingerGesture.value = false;
        restoreToolStateAfterGesture(localCanvas);
      }
      return;
    }

    if (selectedTool.value === CANVAS_ACTION.MOVE) {
      isDragging.value = true;
      localCanvas.selection = false;
      lastPosX.value = getClientX(event);
      lastPosY.value = getClientY(event);
    } else if (
      selectedTool.value === DRAWING_TOOL.PEN ||
      selectedTool.value === DRAWING_TOOL.HIGHLIGHTER
    ) {
      if ("touches" in event) {
        const previousTouchCount = activeTouchCount.value;
        activeTouchCount.value = event.touches.length;
        if (event.touches.length === 2 && previousTouchCount < 2 && !isTwoFingerGesture.value) {
          initializeTwoFingerGesture(localCanvas, event.touches[0], event.touches[1]);
          return;
        }
        if (event.touches.length !== 1) {
          return;
        }
      }
      isDrawing.value = true;
      localCanvas.selection = false;
      isDrawingInvalid.value = isPointerInDrawingArea(event);
    } else if (selectedTool.value === DRAWING_TOOL.TEXT_BOX) {
      // Add textbox at click position
      const pointer = localCanvas.getScenePoint(event);
      addTextBox(pointer);
    }
  });

  // Attach touch event listeners (handlers defined at component scope)
  wrapper.value.addEventListener("touchstart", handleTouchStart);
  wrapper.value.addEventListener("touchmove", handleTouchMove, { passive: false });
  // Keep on window: touch can end/cancel outside canvas wrapper on mobile
  window.addEventListener("touchend", handleTouchEnd);
  window.addEventListener("touchcancel", handleTouchCancel);

  localCanvas.on("mouse:up", (option: CanvasEvents["mouse:up"]) => {
    const event = option.e;
    event.preventDefault();
    localCanvas.setViewportTransform(localCanvas.viewportTransform!);
    isDragging.value = false;
    isDrawing.value = false;
    localCanvas.selection = true;

    // undo last invalid drawing
    if (isDrawingInvalid.value) {
      state.value.history.pop();
      const previousState = getLatestHistoryState();
      if (previousState != null) loadCanvasToState(previousState);
      (canvas.value as unknown as { isDrawingInvalid: boolean }).isDrawingInvalid = false;
    }
  });

  localCanvas.on("mouse:move", (option: CanvasEvents["mouse:move"]) => {
    const event = option.e;
    event.preventDefault();

    // Update eraser outline position
    if (selectedTool.value === DRAWING_TOOL.ERASER) {
      if (!(event.target instanceof HTMLElement)) return;
      const rect = event.target.getBoundingClientRect();
      eraserOutlinePosition.value = {
        x: getClientX(event) - rect.left,
        y: getClientY(event) - rect.top,
      };
      eraserOutlineVisible.value = true;
    }

    if (isDragging.value) {
      handleCanvasPanningArea(event);
      localCanvas.requestRenderAll();
      lastPosX.value = getClientX(event);
      lastPosY.value = getClientY(event);
    } else if (isDrawing.value && !isDrawingInvalid.value) {
      isDrawingInvalid.value = isPointerInDrawingArea(event);
    }
  });

  localCanvas.on("mouse:out", () => {
    eraserOutlineVisible.value = false;
  });

  const mouseWheelCanvas = canvas.value as Canvas;
  mouseWheelCanvas.on("mouse:wheel", (option) => {
    const event = option.e;
    event.preventDefault();
    event.stopPropagation();

    const deltaX = event.deltaX;
    const deltaY = event.deltaY;

    const panSpeed = 1;

    const vpt = mouseWheelCanvas.viewportTransform!;

    if (event.ctrlKey) {
      const isZoomIn = deltaY < 0;
      if (isZoomIn && isZoomMax.value) return;
      if (!isZoomIn && isZoomMin.value) return;

      const zoomValue = isZoomIn ? CANVAS_SIZE.ZOOM_IN : CANVAS_SIZE.ZOOM_OUT;

      const zoomChange = zoomPercentage.value + zoomValue;
      const zoomSize = calculateZoomSize(zoomChange);

      const canvasPoint = new Point(event.offsetX, event.offsetY);
      mouseWheelCanvas.zoomToPoint(canvasPoint, zoomSize);
      currentZoom.value = zoomSize;

      handleCanvasViewportTranslation();
    } else {
      vpt[4] -= deltaX * panSpeed; // horizontal (trackpad)
      vpt[5] -= deltaY * panSpeed; // vertical

      handleCanvasViewportTranslation();
    }
    mouseWheelCanvas.requestRenderAll();
  });
}

/**
 * Handle binding on canvas focus.
 */
function bindCanvasFocus() {
  const drawingCanvasModal = document.querySelector(".drawing-canvas-modal");
  if (drawingCanvasModal) {
    drawingCanvasModal.addEventListener("focus", handleCanvasObjUnselected);
  }
}

/**
 * Handle binding on canvas attach image.
 */
function bindCanvasAttachImage() {
  reader.value = new FileReader();
  reader.value.onload = (event) => {
    FabricImage.fromURL((event?.target as FileReader).result as string).then(addImageToCanvas);
  };
}

/**
 * Function to add image to the center of the canvas (current visible area),
 * select the image and zoom canvas to fit the entire image within canvas bounds
 * @param img image
 */
function addImageToCanvas(img: FabricImage) {
  const localCanvas = fabricCanvas as Canvas;
  localCanvas.add(img);
  localCanvas.viewportCenterObject(img);
  localCanvas.setActiveObject(img);
  const zoomSize = calculateZoomSizeOnAttachImage(img);
  localCanvas.zoomToPoint(calculateCanvasCenterPoint(), zoomSize);
  currentZoom.value = zoomSize;
  // Re-align viewport translation so the image stays centered after zoom is capped at 100%
  handleCanvasViewportTranslation();
  localCanvas.renderAll();
  setSelectedDrawingTool(CANVAS_ACTION.SELECT);
  setDrawingMode(false);

  // Save canvas state
  saveCanvasState();
}

function addBaseImageToCanvas(img: FabricImage) {
  const localCanvas = fabricCanvas as Canvas;
  setBaseImageConfig(img);
  localCanvas.add(img);
  localCanvas.viewportCenterObject(img);
  const zoomSize = calculateZoomSizeOnAttachImage(img);
  localCanvas.zoomToPoint(calculateCanvasCenterPoint(), zoomSize);
  currentZoom.value = zoomSize;
  // Re-align viewport translation so the image stays centered after zoom is capped at 100%
  handleCanvasViewportTranslation();
  localCanvas.renderAll();
  syncInitialCanvasState();
}

/**
 * Function to modifies canvas viewport translation enabled canvas panning feature.
 */
function handleCanvasPanningArea(event: MouseEvent | TouchEvent) {
  const localCanvas = canvas.value as Canvas;
  const vpt = localCanvas.viewportTransform!;
  vpt[4] += getClientX(event) - lastPosX.value;
  vpt[5] += getClientY(event) - lastPosY.value;
  handleCanvasViewportTranslation();
}

/**
 * Function to set canvas viewport transform translationX and translationY.
 * 1. Handle canvas view when zoom out cause view bigger than boundary.
 * 2. Limit canvas panning area to avoid canvas view goes inifinite when keep move in same direction.
 *
 * Each element in the transformation matrix represents
 * [scaleX, skewY, skewX, scaleY, translationX, translationY]
 */
function handleCanvasViewportTranslation() {
  const localCanvas = fabricCanvas as Canvas;
  /**
   * get translation value when canvas view within boundary.
   * @param currentTranslation translationX | translationY from canvas viewport transform
   * @param size canvas width | canvas height
   * @param boundarySize boundary size in zoom%
   */
  const getTranslationSize = (currentTranslation: number, size: number, boundarySize: number) => {
    const translation = size - boundarySize;
    if (currentTranslation >= 0) return 0;
    else if (currentTranslation < translation) return translation;
    return currentTranslation;
  };

  /**
   * get translation value when canvas view is greater than boundary.
   * @param size canvas width | canvas height
   * @param boundarySize boundary size in zoom%
   */
  const getTranslationBoundaryOverview = (size: number, boundarySize: number) => {
    return size / 2 - boundarySize / 2;
  };

  const vpt = localCanvas.viewportTransform!;
  const boundaryWidthSize = localCanvas.getZoom() * CANVAS_SIZE.BOUNDARY_WIDTH; // boundary size in zoom%
  const boundaryHeightSize = localCanvas.getZoom() * CANVAS_SIZE.BOUNDARY_HEIGHT; // boundary size in zoom%
  const canvasWidth = localCanvas.getWidth();
  const canvasHeight = localCanvas.getHeight();
  vpt[4] =
    canvasWidth > boundaryWidthSize
      ? getTranslationBoundaryOverview(canvasWidth, boundaryWidthSize)
      : getTranslationSize(vpt[4], canvasWidth, boundaryWidthSize);
  vpt[5] =
    canvasHeight > boundaryHeightSize
      ? getTranslationBoundaryOverview(canvasHeight, boundaryHeightSize)
      : getTranslationSize(vpt[5], canvasHeight, boundaryHeightSize);
}

/**
 * Function to handle canvas / tools when a canvas object has been un-selected.
 */
function handleCanvasObjUnselected() {
  if (selectedTool.value === DRAWING_TOOL.IMAGE) {
    // if current active tool is image, switch to cursor tool.
    setSelectedDrawingTool(CANVAS_ACTION.SELECT);
    setDrawingMode(false);
  }
}

/**
 * Function to handle when a tool/color has been selected.
 */
function handleToolSelected(toolData: { tool: string; value: string }) {
  isMoveEnabled.value = false;
  const localCanvas = canvas.value as Canvas;
  localCanvas.discardActiveObject();
  localCanvas.renderAll();

  if (toolData.tool === "drawingTools") {
    // set active drawing tool and drawing mode depending on the selected tool
    setSelectedDrawingTool(toolData.value);
    setDrawingMode(isFreeDrawingBrush(toolData.value));

    // Restore config for the selected tool
    if (toolData.value === DRAWING_TOOL.PEN) {
      if (brush.value) {
        brush.value.width = penConfig.value.width;
        setSelectedDrawingToolColor(penConfig.value.color);
      }
    } else if (toolData.value === DRAWING_TOOL.HIGHLIGHTER) {
      if (brush.value) {
        brush.value.width = highlighterConfig.value.width;
        setSelectedDrawingToolColor(highlighterConfig.value.color);
      }
    } else if (toolData.value === DRAWING_TOOL.ERASER) {
      if (eraser.value) {
        eraser.value.width = eraserConfig.value.width;
      }
    }

    if (toolData.value === DRAWING_TOOL.IMAGE) {
      // trigger file uploader programmatically to open file selection prompt
      modal.show(
        FileUploaderModal,
        {
          uploadFlow: UploadFlow.IMG,
          modalId: randomUtils.randomId(),
        },
        {
          "update:fileEntry": handleAddedFile,
          fileChanged: (file: FileUploadEntry) => {
            uploadedFile.value = file;
          },
        },
        true,
      );
    }
    if (toolData.value === DRAWING_TOOL.TEXT_BOX) {
      setDrawingMode(false);
    }
  }
}

/**
 * Function to handle tool configuration changes from toolbar.
 */
function handleToolConfigChange(data: ToolConfig) {
  const localCanvas = canvas.value as Canvas;
  if (!localCanvas) return;

  // Update textbox config
  if (data.type === DRAWING_TOOL.TEXT_BOX) {
    textBoxConfig.value = { ...(data.config as TextBoxConfig) };
    return;
  }

  // Update eraser config
  if (data.type === DRAWING_TOOL.ERASER) {
    const config = data.config as EraserConfig;
    eraserConfig.value = { width: config.width };

    if (eraser.value) {
      eraser.value.width = config.width;
    }

    if (localCanvas.isDrawingMode && localCanvas.freeDrawingBrush) {
      localCanvas.freeDrawingBrush = eraser.value as EraserBrush;
    }
    return;
  }

  // Update brush config (for pencil/highlighter)
  if (data.type === DRAWING_TOOL.PEN || data.type === DRAWING_TOOL.HIGHLIGHTER) {
    const config = data.config as BrushConfig;

    if (data.type === DRAWING_TOOL.PEN) {
      penConfig.value = { ...config };
    } else {
      highlighterConfig.value = { ...config };
    }

    if (brush.value) {
      brush.value.width = config.width;
      let brushColor = config.color;
      if (data.type === DRAWING_TOOL.HIGHLIGHTER) {
        brushColor = ColorUtils.hexToRGB(config.color, BRUSH_OPACITY.HIGHLIGHTER);
      } else {
        brushColor = ColorUtils.hexToRGB(config.color, BRUSH_OPACITY.PEN);
      }
      brush.value.color = brushColor as string;
    }

    if (localCanvas.isDrawingMode && localCanvas.freeDrawingBrush) {
      localCanvas.freeDrawingBrush = brush.value as PencilBrush;
    }
  }
}

/**
 * Function to determine if a tool is a canvas brush tool.
 */
function isFreeDrawingBrush(tool: string) {
  return (
    tool === DRAWING_TOOL.PEN || tool === DRAWING_TOOL.HIGHLIGHTER || tool === DRAWING_TOOL.ERASER
  );
}

/**
 * Get client X from event object.
 */
function getClientX(event: MouseEvent | TouchEvent) {
  if ("clientX" in event) return event.clientX;
  if ("touches" in event && event.touches.length > 0) return event.touches[0].clientX;
  return 0;
}

/**
 * Get client Y from event object.
 */
function getClientY(event: MouseEvent | TouchEvent) {
  if ("clientY" in event) return event.clientY;
  if ("touches" in event && event.touches.length > 0) return event.touches[0].clientY;
  return 0;
}

function isPointerInDrawingArea(event: MouseEvent | TouchEvent) {
  const localCanvas = canvas.value as Canvas;
  const pointer = localCanvas.getScenePoint(event);
  return (
    pointer.x < 0 ||
    pointer.y < 0 ||
    pointer.x > CANVAS_SIZE.BOUNDARY_WIDTH ||
    pointer.y > CANVAS_SIZE.BOUNDARY_HEIGHT
  );
}

/**
 * Function to set the drawing on/off
 */
function setDrawingMode(isDrawingModeOn: boolean) {
  const localCanvas = fabricCanvas as Canvas;
  isDrawingEnabled.value = isDrawingModeOn;
  localCanvas.isDrawingMode = isDrawingModeOn;
  localCanvas.selection = !isDrawingModeOn;
}

/**
 * Function to set the tool for drawing.
 */
function setSelectedDrawingTool(tool: string) {
  const localCanvas = fabricCanvas as Canvas;
  if (!localCanvas) {
    return;
  }
  selectedTool.value = tool;

  // Switch between eraser and regular brush
  if (tool === DRAWING_TOOL.ERASER) {
    localCanvas.freeDrawingBrush = eraser.value as EraserBrush;
  } else {
    localCanvas.freeDrawingBrush = brush.value as PencilBrush;
  }

  localCanvas.freeDrawingBrush!.width = getToolSize(tool);

  const isDrawingTool = isFreeDrawingBrush(tool);
  const isMoveSelected = tool === CANVAS_ACTION.MOVE;
  const isObjectsSelectable = !isDrawingTool && !isMoveSelected;

  localCanvas.getObjects().forEach((object) => {
    if ((object as CanvasObjectWithBaseImageFlag).isBaseImage) {
      setBaseImageConfig(object as FabricImage);
      return;
    }
    object.selectable = isObjectsSelectable;
    object.evented = isObjectsSelectable;
    object.hoverCursor = isMoveSelected ? "pointer" : "move";
  });
}

/**
 * Function to get configured size of the drawing tool size.
 */
function getToolSize(toolName: string) {
  const tool = props.tools.filter((t) => {
    return t.name === toolName;
  })[0];

  return tool ? tool.size : 0;
}

/**
 * Function to set the colour for the drawing tool.
 */
function setSelectedDrawingToolColor(color: string) {
  const localCanvas = canvas.value as Canvas;
  if (!localCanvas) {
    return;
  }
  localCanvas.freeDrawingBrush = brush.value as PencilBrush;

  let brushColor = color;
  if (selectedTool.value === DRAWING_TOOL.HIGHLIGHTER) {
    // Highlighter: 30% opacity
    brushColor = ColorUtils.hexToRGB(color, BRUSH_OPACITY.HIGHLIGHTER);
  } else if (selectedTool.value === DRAWING_TOOL.PEN) {
    // Pen: 100% opacity
    brushColor = ColorUtils.hexToRGB(color, BRUSH_OPACITY.PEN);
  }

  localCanvas.freeDrawingBrush!.color = brushColor as string;
}

/**
 * Function to handle when a drawing action has been selected.
 */
function handleDrawingAction(action: string, value?: number) {
  isMoveEnabled.value = false;

  switch (action) {
    case CANVAS_ACTION.SELECT: {
      setSelectedDrawingTool(CANVAS_ACTION.SELECT);
      setDrawingMode(false);
      break;
    }
    case CANVAS_ACTION.MOVE: {
      setSelectedDrawingTool(CANVAS_ACTION.MOVE);
      setDrawingMode(false);
      isMoveEnabled.value = true;
      break;
    }
    case CANVAS_ACTION.UNDO: {
      undoPreviousAction();
      break;
    }
    case CANVAS_ACTION.REDO: {
      redoNextAction();
      break;
    }
    case CANVAS_ACTION.DELETE: {
      removeSelectedCanvasObjs();
      break;
    }
    case CANVAS_ACTION.FORWARD: {
      moveObjectForward();
      break;
    }
    case CANVAS_ACTION.BACKWARD: {
      moveObjectBackward();
      break;
    }
    case CANVAS_ACTION.CLEAR: {
      clearCanvas();
      break;
    }
    case CANVAS_ACTION.ZOOM_IN:
    case CANVAS_ACTION.ZOOM_OUT: {
      const isZoomIn = action === CANVAS_ACTION.ZOOM_IN;
      handleZoomAction(isZoomIn);
      break;
    }
    case CANVAS_ACTION.ZOOM_FIT: {
      handleZoomFitToWidth();
      break;
    }
    case CANVAS_ACTION.ZOOM_SLIDE: {
      handleCanvasZoom(value as number);
      break;
    }
    case CANVAS_ACTION.ZOOM_DEFAULT: {
      // sync up: remove reset zoom to default feature. TBC.
      // this.handleZoomDefault();
      break;
    }
  }
}

/**
 * Handle the file after uploaded by File Uploader.
 * @param addedFile {File | undefined}
 */
function handleAddedFile(fileResponse: FileResponse) {
  const addedFile = uploadedFile.value?.file;
  if (addedFile && fileResponse.status == FileStatus.READY) {
    reader.value?.readAsDataURL(addedFile);
    fileUpload.value?.handleRemove();
  }
}

/**
 * Function to add a new text box into the canvas.
 * @param position Optional position to place the textbox. If not provided, centers it.
 */
function addTextBox(position?: Point) {
  const localCanvas = canvas.value as Canvas;
  // Populate text config
  const textBoxSettings: FabricTextBoxSettings = {
    ...(props.textBoxSettings as { textBox?: FabricTextBoxSettings }).textBox,
  };
  textBoxSettings.fill = textBoxConfig.value.color;
  textBoxSettings.erasable = true;

  // Set position if provided
  if (position) {
    textBoxSettings.left = position.x;
    textBoxSettings.top = position.y;
  }

  const fabricTextBox = markRaw(
    new Textbox((props.textBoxSettings as { placeholderText?: string }).placeholderText as string, {
      ...textBoxSettings,
      originX: "center",
      originY: "center",
    }),
  );
  //line 28158 js default dbl click event didnt call focus on hiddenTextarea hence its not focusable on mobile(SLSSIT-4687)
  fabricTextBox.on("mousedblclick", () => {
    fabricTextBox.hiddenTextarea?.focus();
  });

  fabricTextBox.on("selection:changed", () => {
    setTimeout(() => {
      fabricTextBox.hiddenTextarea?.focus();
    }, 300);
  });
  // Add to canvas
  localCanvas.add(fabricTextBox);

  // Center only if no position was provided
  if (!position) {
    localCanvas.viewportCenterObject(fabricTextBox);
  }

  localCanvas.setActiveObject(fabricTextBox);
  fabricTextBox.enterEditing();
  fabricTextBox.selectAll();
  setSelectedDrawingTool(CANVAS_ACTION.SELECT);

  // Save canvas state
  saveCanvasState();
}

/**
 * Function to remove the selected canvas object(s) (image/text/strokes).
 */
function removeSelectedCanvasObjs() {
  const localCanvas = canvas.value as Canvas;
  if (localCanvas.getActiveObjects().length === 0) return;
  localCanvas.getActiveObjects().forEach((activeObject) => localCanvas.remove(activeObject));
  localCanvas.discardActiveObject();
  localCanvas.renderAll();
  saveCanvasState();
}

/**
 * Function to move canvas object forward.
 */
function moveObjectForward() {
  const localCanvas = canvas.value as Canvas;
  const activeObject = localCanvas.getActiveObject() as FabricObject;
  if (isEmpty(activeObject)) return;
  localCanvas.bringObjectForward(activeObject, true);
  saveCanvasState();
}

/**
 * Function to move canvas object backward.
 */
function moveObjectBackward() {
  const localCanvas = canvas.value as Canvas;
  const activeObject = localCanvas.getActiveObject() as FabricObject;
  if (isEmpty(activeObject)) return;
  localCanvas.sendObjectBackwards(activeObject, true);
  saveCanvasState();
}

/**
 * Function to save canvas state.
 */
function saveCanvasState() {
  const localCanvas = fabricCanvas as Canvas;
  // set dirty
  isDirty.value = true;

  // clear cache if exist, user will not be able to undo clear data anymore.
  clearCache();

  // clear undo history, user will not be able to load previous undoed canvas action.
  clearUndoHistory();

  // store new current state
  state.value.history.push(localCanvas.toDatalessJSON() as unknown as Record<string, unknown>);
}

/**
 * Function to load the canvas to a particular saved state.
 */
function loadCanvasToState(s: Record<string, unknown>) {
  const localCanvas = fabricCanvas as Canvas;
  localCanvas
    .loadFromJSON(s, (object) => {
      if (object instanceof Path) {
        object.set(props.strokeSettings);
      } else if (object instanceof Textbox) {
        const textBox = object as Textbox;
        const textBoxSettings: FabricTextBoxSettings = {
          ...(props.textBoxSettings as { textBox?: FabricTextBoxSettings }).textBox,
        };
        delete textBoxSettings.width; // do not reset width
        textBox.set(textBoxSettings);
        textBox.on("selection:changed", function () {
          // setTimeout to ensure the hiddenTextarea is focused after fabricJS add it into the DOM
          setTimeout(() => {
            textBox.hiddenTextarea!.focus();
          }, 300);
        });
      }
    })
    .then(() => {
      const baseImage = findBaseImageObject(localCanvas.getObjects());
      if (baseImage) {
        setBaseImageConfig(baseImage);
      }
      localCanvas.renderAll();
    });
}

/**
 * Function to undo previous canvas action.
 */
function undoPreviousAction() {
  if (!canUndo.value) {
    return;
  }

  let previousState: Record<string, unknown> | undefined = undefined;
  if (!isHistoryEmpty()) {
    // normal undo
    state.value.undoHistory.push(state.value.history.pop() as Record<string, unknown>);
    previousState = getLatestHistoryState() as Record<string, unknown> | undefined;
  } else if (!isCacheEmpty()) {
    // undo previous clear action.
    state.value.undoHistory.push(state.value.initial as Record<string, unknown>);
    reloadFromCache();
    previousState = getLatestHistoryState() as Record<string, unknown> | undefined;
  }

  if (previousState != null) {
    loadCanvasToState(previousState);
  }
}

/**
 * Function to redo next canvas action.
 */
function redoNextAction() {
  if (isUndoHistoryEmpty()) return;
  const nextState = state.value.undoHistory.pop();
  if (nextState == null) return;
  if (nextState === state.value.initial) cacheHistory();
  else state.value.history.push(nextState);
  loadCanvasToState(nextState);
}

/**
 * Function to clear the canvas.
 */
function clearCanvas() {
  if (!canClear.value || !isCacheEmpty()) {
    // case when previous action is to clear canvas, do nothing
    return;
  }

  const confirmationMessage = hasBackgroundImage()
    ? "drawing_canvas_clear_all_with_base_image_confirmation_message"
    : "drawing_canvas_clear_all_confirmation_message";

  if (!confirm(t(confirmationMessage))) {
    // cancelled clear confirmation
    return;
  }

  cacheHistory();
  clearUndoHistory();
  loadCanvasToState(state.value.initial as Record<string, unknown>);
}

/**
 * Function to calculate zoom size after zoom in and zoom out
 */
function calculateZoomSize(zoomChange: number) {
  if (zoomChange > CANVAS_SIZE.ZOOM_MAX) return CANVAS_SIZE.ZOOM_MAX / CANVAS_SIZE.ZOOM_PERCENTAGE;
  if (zoomChange < CANVAS_SIZE.ZOOM_MIN) return CANVAS_SIZE.ZOOM_MIN / CANVAS_SIZE.ZOOM_PERCENTAGE;
  return zoomChange / CANVAS_SIZE.ZOOM_PERCENTAGE;
}

/**
 * Function to calculate zoom size during add new image
 */
function calculateZoomSizeOnAttachImage(imgObj: FabricImage) {
  const canvasHeight = (imgObj as unknown as { canvas: { height: number; width: number } }).canvas
    .height;
  const canvasWidth = (imgObj as unknown as { canvas: { height: number; width: number } }).canvas
    .width;

  const imageHeight = imgObj.height!;
  const imageWidth = imgObj.width!;

  // Compute zoom percentage for both dimensions
  const zoomPctHeight = (canvasHeight / imageHeight) * CANVAS_SIZE.ZOOM_PERCENTAGE;
  const zoomPctWidth = (canvasWidth / imageWidth) * CANVAS_SIZE.ZOOM_PERCENTAGE;

  // Pick the smaller zoom to fit inside canvas
  let zoomPct = Math.min(zoomPctHeight, zoomPctWidth);
  // Round down zoom percentage
  zoomPct = Math.floor(zoomPct / 10) * 10;
  // Cap zoom at 100% so images are never upscaled beyond original size after fit-to-canvas.
  zoomPct = Math.min(zoomPct, CANVAS_SIZE.ZOOM_PERCENTAGE);
  if (zoomPct < CANVAS_SIZE.ZOOM_MIN) return CANVAS_SIZE.ZOOM_MIN / CANVAS_SIZE.ZOOM_PERCENTAGE;
  return zoomPct / CANVAS_SIZE.ZOOM_PERCENTAGE;
}

/**
 * Function to handle reset zoom to default value
 */
function handleZoomDefault() {
  const localCanvas = canvas.value as Canvas;
  localCanvas.zoomToPoint(canvasBoundaryCenterPoint.value, CANVAS_SIZE.ZOOM_DEFAULT);
  currentZoom.value = CANVAS_SIZE.ZOOM_DEFAULT;
  const vpt = localCanvas.viewportTransform!;
  const translationX = localCanvas.getWidth() - CANVAS_SIZE.BOUNDARY_WIDTH;
  const translationY = localCanvas.getHeight() - CANVAS_SIZE.BOUNDARY_HEIGHT;
  vpt[4] = translationX / 2;
  vpt[5] = translationY / 2;
}

/**
 *  Handle Zoom actions on mobile
 */
function handleZoomAction(isZoomIn: boolean | 0 | 1) {
  if ((isZoomIn === true || isZoomIn === 1) && isZoomMax.value) return;
  if ((isZoomIn === false || isZoomIn === 0) && isZoomMin.value) return;
  const zoomValue = isZoomIn ? CANVAS_SIZE.ZOOM_IN : CANVAS_SIZE.ZOOM_OUT;
  const zoomChange = zoomPercentage.value + zoomValue;
  handleCanvasZoom(zoomChange);
}

function handleZoomFitToWidth() {
  const localCanvas = fabricCanvas as Canvas;
  const objects = localCanvas.getObjects();
  if (objects.length === 0) return;

  const canvasSelection = new ActiveSelection(objects, {
    canvas: localCanvas,
  });
  // Set as active object to prevent position jump
  localCanvas.setActiveObject(canvasSelection);

  const zoomPct =
    (((canvasSelection as unknown as { canvas: { width: number } }).canvas.width as number) /
      canvasSelection.width!) *
    CANVAS_SIZE.ZOOM_PERCENTAGE;

  // pan to center of objects horizontally
  // y axis is kept to top of viewport unless the image is out of viewport after zoom in
  localCanvas.setZoom(calculateZoomSize(zoomPct));
  const isOutOfViewport = canvasSelection.top! >= localCanvas.calcViewportBoundaries().br.y;
  const canvasCenterPoint = calculateCanvasCenterPoint();
  const x = canvasSelection.getCenterPoint().x - canvasCenterPoint.x;
  const y = isOutOfViewport
    ? canvasSelection.getCenterPoint().y - canvasCenterPoint.y / 2
    : localCanvas.calcViewportBoundaries().tl.y;
  // reset zoom so pan actions work as expected
  localCanvas.setZoom(1);
  localCanvas.absolutePan(new Point(x, y));

  handleCanvasZoom(zoomPct);
  localCanvas.discardActiveObject();
  localCanvas.renderAll();
}

function handleZoomSliderChange(e: Event) {
  handleCanvasZoom(Number((e.target as HTMLInputElement).value));
}

function handleCanvasZoom(value: number) {
  const localCanvas = canvas.value as Canvas;
  const zoomSize = calculateZoomSize(value);
  const zoomPoint = new Point(localCanvas.getWidth() / 2, 0);
  localCanvas.zoomToPoint(zoomPoint, zoomSize);
  currentZoom.value = zoomSize;
  handleCanvasViewportTranslation();
}

/**
 * Function to determine if the canvas history cache is empty.
 */
function isCacheEmpty() {
  return state.value.cache.length === 0;
}

/**
 * Function to cache the current canvas history.
 */
function cacheHistory() {
  state.value.cache = state.value.history;
  clearHistory();
}

/**
 * Function to clear the canvas history cache.
 */
function clearCache() {
  state.value.cache = [];
}

/**
 * Function to reload canvas history from the cache.
 */
function reloadFromCache() {
  state.value.history = state.value.cache;
  clearCache();
}

/**
 * Function to check if the drawing history is empty.
 */
function isHistoryEmpty() {
  return state.value.history.length === 0;
}

/**
 * Function to check if the undo history is empty
 */
function isUndoHistoryEmpty() {
  return state.value.undoHistory.length === 0;
}

/**
 * Function to retrieve the latest history state stored.
 * If stored state array is empty, return the initial state.
 */
function getLatestHistoryState() {
  return isHistoryEmpty()
    ? state.value.initial
    : state.value.history[state.value.history.length - 1];
}

/**
 * Function to clear history.
 */
function clearHistory() {
  state.value.history = [];
}

/**
 * Function to clear undo history.
 */
function clearUndoHistory() {
  state.value.undoHistory = [];
}

/**
 * Function to convert canvas drawing to image BLOB.
 * A new canvas loaded the background and object(s) is constructed for saving so that the
 * resultant image will not be affected by scaled canvas (due to screen size)
 */
function getCanvasImageFile(): Blob {
  const localCanvas = fabricCanvas as Canvas;
  const canvasSelection = new ActiveSelection(localCanvas.getObjects(), {
    canvas: localCanvas,
  });
  // Generate image from canvas
  return drawingUtils.serializeCanvasToBlob(canvasSelection, {
    fileType: props.exportFileType,
    multiplier: props.exportImageScaleValue,
  }) as Blob;
}

function resizeCanvas() {
  const localCanvas = fabricCanvas as Canvas;
  const wrapper = DOMUtils.el(".canvas-group") as HTMLElement;
  if (!isDefined(wrapper)) return;

  const wrapperWidth = wrapper.clientWidth;
  const wrapperHeight = wrapper.clientHeight;

  nextTick(() => {
    localCanvas.setDimensions({ width: wrapperWidth, height: wrapperHeight });
    handleCanvasViewportTranslation();
  });
}

function toggleZoomActions() {
  zoomActionsExpanded.value = !zoomActionsExpanded.value;
}

function handleCanvasClick() {
  // Hide tooltip when clicking on canvas
  (document.activeElement as HTMLElement | null)?.blur();
}
</script>

<style lang="scss">
@import "@styles/layout/breakpoint";
@import "@styles/layout/z-map";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.drawing-canvas {
  &.move-enabled {
    .canvas-group {
      canvas {
        cursor: grab !important;
      }
    }
  }

  &.textbox-enabled {
    .canvas-group {
      canvas {
        cursor: crosshair !important;
      }
    }
  }

  &.eraser-enabled {
    .canvas-group {
      canvas {
        cursor: none !important;
      }
    }
  }

  @include media("tablet-min") {
    display: flex;
    position: relative;

    .drawing-canvas-toolbar {
      flex: 0 0 8rem;
      max-width: 8rem;
    }

    .canvas-group {
      flex: 1 1 0%;
      margin: 3.5rem 0 $spacing-05;
      max-width: calc(100% - 8rem);

      .canvas-wrapper {
        width: 100%;
      }
    }
  }

  .file-uploader {
    display: none;
  }

  .canvas-group {
    position: relative;
    background-color: $ui-01;
    border-radius: 10px;
    z-index: 0;

    @include media("mobile") {
      min-height: 455px;

      .canvas-wrapper {
        height: 100%;
      }
    }

    > .zoom-actions {
      display: flex;
      align-items: center;
      top: 1rem;
      right: 1rem;
      height: 56px;
      padding: $spacing-04 $spacing-05;
      background-color: $ui-background;
      text-align: center;
      box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.2);
      border-radius: 10px;

      @include media("tablet-min") {
        display: none;
      }

      button {
        height: 24px;
      }

      &.expanded {
        .toggle-expand {
          margin-right: $spacing-06;
        }
      }
    }

    .canvas-wrapper {
      position: absolute;
      border-radius: inherit;

      &.background {
        z-index: -1;
      }
    }

    .canvas-container {
      border-radius: inherit;
    }

    canvas {
      border-radius: inherit;
      touch-action: none;
    }

    .eraser-outline {
      @include media("desktop") {
        position: absolute;
        border: 1px solid $inverse-02;
        background-color: $ui-01;
        box-sizing: border-box;
        pointer-events: none;
        z-index: calc(#{z-map("default")} + 1);
      }
    }
  }

  /* There are two zoom-actions, one in DrawingCanvas and another in DrawingCanvasToolbar
   * This is the shared styling for both of them
   */
  .zoom-actions {
    position: absolute;
    z-index: z-map("default");

    button {
      &.zoom-fit {
        &:hover {
          svg {
            fill: none;
          }
        }
      }
    }

    .sls-icon {
      [fill] {
        fill: $interactive-01;
      }
    }

    .zoom-slider {
      appearance: none;
      height: 2px;
      width: 72px;
      background: $ui-04;
      border-radius: 10px;

      &::-webkit-slider-thumb {
        appearance: none;
        background: $interactive-01;
        height: 16px;
        width: 16px;
        border-radius: 100%;
      }
    }
  }
}
</style>
